// src/features/auth/types/auth.types.ts

/**
 * User role types
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  HR_MANAGER = 'hr_manager',
  HR_STAFF = 'hr_staff',
  MANAGER = 'manager',
  EMPLOYEE = 'employee',
  GUEST = 'guest',
}

/**
 * User status
 */
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

/**
 * Authentication provider types
 */
export enum AuthProvider {
  EMAIL = 'email',
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  GITHUB = 'github',
  SAML = 'saml',
  LDAP = 'ldap',
  PASSWORDLESS = 'passwordless',
  INVITE = 'invite',
  COMPANY_CODE = 'companyCode',
}

/**
 * MFA method types
 */
export enum MFAMethod {
  TOTP = 'totp',
  SMS = 'sms',
  EMAIL = 'email',
  BACKUP_CODES = 'backup_codes',
}

/**
 * Base user information
 */
export interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  phone?: string;
  phoneVerified?: boolean;
  role: UserRole;
  roles?: UserRole[]; // For multiple roles
  status: UserStatus;
  jobTitle?: string;
  department?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: UserMetadata;
  onboardingCompleted?: boolean;
}

/**
 * Authentication tokens
 */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType?: string;
  scope?: string;
}

/**
 * User metadata
 */
export interface UserMetadata {
  department?: string;
  jobTitle?: string;
  employeeId?: string;
  managerId?: string;
  location?: string;
  timezone?: string;
  language?: string;
  preferences?: UserPreferences;
  mfaEnabled?: boolean;
}

/**
 * User preferences
 */
export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system';
  notifications?: NotificationPreferences;
  locale?: string;
  dateFormat?: string;
  timeFormat?: '12h' | '24h';
}

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  email?: boolean;
  push?: boolean;
  sms?: boolean;
  inApp?: boolean;
  digest?: 'immediate' | 'daily' | 'weekly' | 'never';
}

/**
 * Authentication tokens
 */
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
  tokenType?: string;
  scope?: string;
}

/**
 * Authentication session
 */
export interface Session {
  id: string;
  userId: string;
  tenantId: string;
  deviceInfo?: DeviceInfo;
  ipAddress?: string;
  location?: string;
  userAgent?: string;
  createdAt: string;
  expiresAt: string;
  lastActivity?: string;
  isActive: boolean;
}

/**
 * Device information
 */
export interface DeviceInfo {
  id: string;
  name: string;
  type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  os?: string;
  browser?: string;
  trusted?: boolean;
  lastSeen?: string;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
  workspace?: string;
  rememberMe?: boolean;
  mfaCode?: string;
  deviceFingerprint?: string;
}

/**
 * Social login credentials
 */
export interface SocialAuthRequest {
  provider: AuthProvider;
  token: string;
  workspace?: string;
  deviceFingerprint?: string;
}

/**
 * Registration data
 */
export interface RegistrationData {
  email: string;
  password: string;
  confirmPassword?: string;
  firstName: string;
  lastName: string;
  workspace?: string;
  inviteToken?: string;
  companyCode?: string;
  termsAccepted: boolean;
  marketingOptIn?: boolean;
}

/**
 * Authentication response
 */
export interface AuthResponse {
  user: User;
  tokens: TokenPair;
  session: Session;
  requiresMFA?: boolean;
  mfaSessionToken?: string;
  workspace?: WorkspaceInfo;
  redirectUrl?: string;
}

/**
 * MFA setup response
 */
export interface MFASetupResponse {
  method: MFAMethod;
  secret?: string;
  qrCode?: string;
  backupCodes?: string[];
  sessionToken: string;
  expiresAt: string;
}

/**
 * MFA verification request
 */
export interface MFAVerifyRequest {
  code: string;
  method?: MFAMethod;
  sessionToken?: string;
  trustDevice?: boolean;
}

/**
 * Password reset request
 */
export interface PasswordResetRequest {
  email: string;
  workspace?: string;
}

/**
 * Password reset confirmation
 */
export interface PasswordResetConfirm {
  token: string;
  password: string;
  confirmPassword: string;
}

/**
 * Email verification request
 */
export interface EmailVerificationRequest {
  token: string;
  email?: string;
}

/**
 * Workspace/Tenant information
 */
export interface WorkspaceInfo {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  domain?: string;
  settings?: WorkspaceSettings;
}

/**
 * Workspace settings
 */
export interface WorkspaceSettings {
  authProviders: AuthProvider[];
  mfaRequired: boolean;
  mfaMethods: MFAMethod[];
  passwordPolicy?: PasswordPolicy;
  sessionTimeout?: number;
  allowRegistration?: boolean;
  emailDomainWhitelist?: string[];
  features?: string[];
}

/**
 * Password policy
 */
export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  preventReuse: number;
  expiryDays?: number;
}

/**
 * Invitation data
 */
export interface InviteData {
  token: string;
  email: string;
  role: UserRole;
  workspace: WorkspaceInfo;
  invitedBy: string;
  expiresAt: string;
  metadata?: Record<string, unknown>;
}

/**
 * Invitation validation response
 */
export interface InviteValidationResponse {
  valid: boolean;
  invite?: InviteData;
  error?: string;
}

/**
 * Workspace validation response
 */
export interface WorkspaceValidationResponse {
  exists: boolean;
  workspace?: WorkspaceInfo;
  authMethods?: AuthProvider[];
  registrationEnabled?: boolean;
}

/**
 * Device trust request
 */
export interface DeviceTrustRequest {
  deviceFingerprint: string;
  deviceName?: string;
  trustDuration?: number; // days
}

/**
 * Refresh token response
 */
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType?: string;
}

/**
 * Permission structure
 */
export interface Permission {
  resource: string;
  action: string;
  conditions?: Record<string, unknown>;
}

/**
 * Role with permissions
 */
export interface RoleWithPermissions {
  role: UserRole;
  permissions: Permission[];
  inherits?: UserRole[];
}


export interface MFABackupCodesResponse {
  backupCodes: string[];
  generatedAt: string;
}

export interface MFAChallengeState {
  required: boolean;
  sessionToken?: string;
  availableMethods: MFAMethod[];
  attemptsRemaining?: number;
  expiresAt?: string;
}

export interface MFADeviceTrust {
  deviceId: string;
  trusted: boolean;
  trustedUntil?: string;
  deviceName?: string;
}


/**
 * Auth state for Redux
 */
export interface AuthState {
  // User data
  user: User | null;
  
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Session data
  session: Session | null;
  tokens: TokenPair | null;
  
  // MFA state
  mfaRequired: boolean;
  mfaSessionToken: string | null;
  
  // Workspace/Tenant
  workspace: WorkspaceInfo | null;
  availableWorkspaces?: WorkspaceInfo[];
  
  // Permissions
  permissions: Permission[];
  
  // UI state
  error: AuthError | null;
  redirectUrl: string | null;
}

/**
 * Auth error types
 */
export interface AuthError {
  code: string;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
}

/**
 * Auth action types for Redux
 */
export enum AuthActionType {
  // Authentication
  LOGIN_REQUEST = 'auth/loginRequest',
  LOGIN_SUCCESS = 'auth/loginSuccess',
  LOGIN_FAILURE = 'auth/loginFailure',
  
  LOGOUT_REQUEST = 'auth/logoutRequest',
  LOGOUT_SUCCESS = 'auth/logoutSuccess',
  LOGOUT_FAILURE = 'auth/logoutFailure',
  
  // Registration
  REGISTER_REQUEST = 'auth/registerRequest',
  REGISTER_SUCCESS = 'auth/registerSuccess',
  REGISTER_FAILURE = 'auth/registerFailure',
  
  // Token management
  REFRESH_TOKEN_REQUEST = 'auth/refreshTokenRequest',
  REFRESH_TOKEN_SUCCESS = 'auth/refreshTokenSuccess',
  REFRESH_TOKEN_FAILURE = 'auth/refreshTokenFailure',
  
  // User data
  FETCH_USER_REQUEST = 'auth/fetchUserRequest',
  FETCH_USER_SUCCESS = 'auth/fetchUserSuccess',
  FETCH_USER_FAILURE = 'auth/fetchUserFailure',
  
  UPDATE_USER = 'auth/updateUser',
  
  // MFA
  MFA_REQUIRED = 'auth/mfaRequired',
  MFA_VERIFY_SUCCESS = 'auth/mfaVerifySuccess',
  MFA_VERIFY_FAILURE = 'auth/mfaVerifyFailure',
  
  // Session
  UPDATE_SESSION = 'auth/updateSession',
  CLEAR_SESSION = 'auth/clearSession',
  
  // Workspace
  SET_WORKSPACE = 'auth/setWorkspace',
  SWITCH_WORKSPACE = 'auth/switchWorkspace',
  
  // Permissions
  SET_PERMISSIONS = 'auth/setPermissions',
  
  // UI
  SET_REDIRECT_URL = 'auth/setRedirectUrl',
  CLEAR_ERROR = 'auth/clearError',
  
  // Initialization
  INITIALIZE = 'auth/initialize',
  INITIALIZE_SUCCESS = 'auth/initializeSuccess',
  INITIALIZE_FAILURE = 'auth/initializeFailure',
}

/**
 * Type guards
 */
export function isUser(obj: unknown): obj is User {
  return obj !== null && typeof obj === 'object' && 'id' in obj && 'email' in obj;
}

export function isTokenPair(obj: unknown): obj is TokenPair {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'accessToken' in obj &&
    'refreshToken' in obj
  );
}

export function isAuthResponse(obj: unknown): obj is AuthResponse {
  return obj !== null && typeof obj === 'object' && 'user' in obj && 'tokens' in obj;
}

/**
 * Utility types
 */
export type PartialUser = Partial<User>;
export type UserUpdate = Omit<PartialUser, 'id' | 'email' | 'createdAt' | 'updatedAt'>;
export type PublicUser = Omit<User, 'metadata' | 'phoneVerified'>;

/**
 * Constants
 */
export const DEFAULT_SESSION_TIMEOUT = 24 * 60 * 60; // 24 hours in seconds
export const DEFAULT_MFA_SESSION_TIMEOUT = 5 * 60; // 5 minutes in seconds
export const DEFAULT_TOKEN_REFRESH_BUFFER = 5 * 60; // 5 minutes in seconds